import React from "react";
import { cn } from "@/lib/utils";
import type { ShellDayCell } from "@/components/modals/CalendarModalShell.types";
import type { SpecificNeedsConstraints } from "@/types/needsContrains";
import MinMaxInput from "./MinMaxInput";
import HourRangeInput from "./HourRangeInput";
import { isApplied as isConfigApplied } from "./needsExceptionUtils";

export type NeedsDayCellChange = (dateStr: string, patch: Partial<SpecificNeedsConstraints>) => void;

interface NeedsExceptionDayCellProps {
    cell: ShellDayCell;
    config: SpecificNeedsConstraints;
    isException: boolean;
    onChange: NeedsDayCellChange;
}

const NeedsExceptionDayCell = React.memo(function NeedsExceptionDayCell(
    {
        cell, config, isException, onChange
    }: Readonly<NeedsExceptionDayCellProps>
) {
    const isApplied = isConfigApplied(config);

    return (
        <div
            className={cn(
                "h-full min-h-[150px] p-2 flex flex-col gap-1",
                isException && "bg-red-50"
            )}
            style={cell.isHoliday && !isException ? { backgroundColor: "var(--holiday-bg)" } : undefined}
        >
            <div className="flex items-baseline justify-between">
                <span className="text-lg font-semibold text-gray-900">{cell.dayLabel}</span>
                {isException && (
                    <span className="text-sm font-bold text-red-600">
                        Exception
                    </span>
                )}
            </div>

            <div className={cn("flex flex-col gap-1", !isApplied && "opacity-50 pointer-events-none grayscale")}>
                <div className="mt-1">
                    <MinMaxInput
                        min={config.min ?? 0}
                        max={config.max ?? 0}
                        disabled={!isApplied}
                        onChange={(patch) => onChange(cell.dateStr, patch)}
                    />
                </div>

                <HourRangeInput
                    start={config.start ?? 0}
                    end={config.end ?? 0}
                    disabled={!isApplied}
                    onChange={(patch) => onChange(cell.dateStr, patch)}
                />
            </div>

            <label className="mt-auto pt-1 flex items-center justify-center gap-2 cursor-pointer">
                <input
                    type="checkbox"
                    checked={isApplied}
                    aria-label={`Activé — ${cell.dayLabel}`}
                    onChange={(e) => onChange(cell.dateStr, { apply: e.target.checked ? "true" : "false" })}
                    className="h-4 w-4 accent-blue-600"
                />
                <span className={cn("text-sm", isException && "text-red-600 font-bold")}>Activé</span>
            </label>
        </div>
    );
});

export default NeedsExceptionDayCell;
